import Header from '@/app/components/Layout/Header/Header'
import Sidebar from '@/app/components/Layout/Sidebar/SideBar'
import { LuxButton } from '@lux/wardrobe-lib'
import { useEffect, useState } from 'react'
import DialogConfirmDeletePhoto from './DialogConfirmDeletePhoto'
import PhotoDetailsUpload from './PhotoDetailsUpload'
import { useIntl } from 'react-intl'
import PhotoSlider from '@/app/components/SavePicture/PhotoSlider'
import { downloadPhoto } from '@/app/utils/commonFunctions'
import './DialogPhotoDetailsUpload.scss'

const DialogPhotoDetailsUpload = ({
    onClose,
    storeId,
    projectType,
    projectName,
    onExpandImage,
    onTag,
    onEdit,
    uploadedImageList,
    onRetakePhoto,
    onSave,
    onAddPhoto,
    onDeleteUploadedPhoto,
    slide,
    setSlide,
}) => {
    const componentClass = 'smart-modals-dialog-photo-details-upload'

    const intl = useIntl()
    const [mobileImageList, setMobileImageList] = useState<any[]>([])
    const [dialogConfirmDeletePhotoVisible, setDialogConfirmDeletePhotoVisible] =
        useState<boolean>(false)
    const [macroareaMap, setMacroareaMap] = useState<Record<string, string>>({
        [uploadedImageList[slide].attachmentId]: uploadedImageList[slide].macroarea || '',
    })
    const [fixtureCountMap, setFixtureCountMap] = useState<Record<string, number>>({
        [uploadedImageList[slide].attachmentId]: uploadedImageList[slide].fixtureCount || 1,
    })

    const previousImage = () => setSlide(slide - 1)

    const nextImage = () => setSlide(slide + 1)

    const onDownload = () => {
        downloadPhoto(
            uploadedImageList[slide]?.url,
            `${uploadedImageList[slide]?.name}.${uploadedImageList[slide]?.ext}`
        )
    }

    const onMacroarea = (macroarea: string) => {
        setMacroareaMap((map) => ({
            ...map,
            [uploadedImageList[slide].attachmentId]: macroarea,
        }))
    }

    const onFixtureCount = (fixtureCount: string) => {
        setFixtureCountMap((map) => ({
            ...map,
            [uploadedImageList[slide].attachmentId]: fixtureCount,
        }))
    }

    const initFixtureCountMap = () => {
        if (uploadedImageList[slide]) {
            setFixtureCountMap((map) => ({
                ...map,
                [uploadedImageList[slide].attachmentId]:
                    map[uploadedImageList[slide].attachmentId] || 1,
            }))
        }
    }

    useEffect(() => {
        setMobileImageList(
            uploadedImageList.map((i) => ({
                photo: {
                    url: i.url,
                },
                actions: [
                    {
                        label: 'expand',
                        icon: 'expand',
                        command: onExpandImage.bind(null, {
                            url: i.url,
                            name: i.name,
                        }),
                    },
                    {
                        label: 'delete',
                        icon: 'trash',
                        command: (val) => setDialogConfirmDeletePhotoVisible(true),
                    },
                ],
            }))
        )
        if (slide > 0 && !uploadedImageList[slide]) setSlide(slide - 1)
        initFixtureCountMap()
    }, [uploadedImageList])

    useEffect(() => {
        setMacroareaMap((map) => ({
            ...map,
            [uploadedImageList[slide].attachmentId]:
                map[uploadedImageList[slide].attachmentId] || '',
        }))
        initFixtureCountMap()
    }, [slide])

    useEffect(() => {
        return () => {
            setMobileImageList([])
            setSlide(0)
        }
    }, [])

    return (
        <div className={componentClass}>
            <div className="header-container">
                <Header onClose={onClose}>
                    <span className="title">{uploadedImageList[slide]?.name}</span>
                </Header>
            </div>
            <div className={`${componentClass}__main`}>
                <div className={`${componentClass}__sidebar`}>
                    <Sidebar>
                        <PhotoDetailsUpload
                            imageId={uploadedImageList[slide].attachmentId}
                            storeId={storeId}
                            projectType={projectType}
                            projectTitle={projectName}
                            isStore
                            macroArea={macroareaMap[uploadedImageList[slide].attachmentId]}
                            onMacroArea={onMacroarea}
                            fixtureCount={fixtureCountMap[uploadedImageList[slide].attachmentId]}
                            onFixtureCount={onFixtureCount}
                        />
                    </Sidebar>
                </div>
                <div className={`${componentClass}__content`}>
                    <PhotoSlider
                        slide={slide}
                        uploadedImageList={uploadedImageList}
                        mobileImageList={mobileImageList}
                        onDownload={onDownload}
                        onTag={onTag.bind(null, {
                            url: uploadedImageList[slide]?.url,
                            name: uploadedImageList[slide]?.name,
                            position: slide + 1,
                        })}
                        onEdit={onEdit.bind(null, {
                            url: uploadedImageList[slide]?.url,
                            name: uploadedImageList[slide]?.name,
                            position: slide + 1,
                        })}
                        onDelete={() => {
                            setDialogConfirmDeletePhotoVisible(true)
                        }}
                        onAddPhoto={onAddPhoto}
                        onExpandImage={onExpandImage}
                        previousImage={previousImage}
                        nextImage={nextImage}
                        onSwiperActiveIndexChange={(swiper) => {
                            setSlide(swiper.activeIndex)
                        }}
                    />
                    <div className={`${componentClass}__details-mobile`}>
                        <PhotoDetailsUpload
                            imageId={uploadedImageList[slide].attachmentId}
                            storeId={storeId}
                            projectType={projectType}
                            projectTitle={projectName}
                            isStore
                            macroArea={macroareaMap[uploadedImageList[slide].attachmentId]}
                            onMacroArea={onMacroarea}
                            fixtureCount={fixtureCountMap[uploadedImageList[slide].attachmentId]}
                            onFixtureCount={onFixtureCount}
                        />
                    </div>
                    <div className={`${componentClass}__footer`}>
                        <LuxButton
                            text={intl.formatMessage({
                                id: 'ph2.modals.photo.retake',
                                defaultMessage: 'Retake photo',
                            })}
                            variant="text"
                            onClick={onRetakePhoto.bind(null, slide + 1)}
                        />
                        <LuxButton
                            text={intl.formatMessage({
                                id: 'ph2.modals.photo.save',
                                defaultMessage: 'Save',
                            })}
                            onClick={() => onSave(macroareaMap, fixtureCountMap)}
                        />
                    </div>
                </div>
            </div>
            <DialogConfirmDeletePhoto
                visible={dialogConfirmDeletePhotoVisible}
                onHide={() => setDialogConfirmDeletePhotoVisible(false)}
                onConfirm={() => {
                    onDeleteUploadedPhoto(uploadedImageList[slide].name)
                    setDialogConfirmDeletePhotoVisible(false)
                }}
            />
        </div>
    )
}

export default DialogPhotoDetailsUpload
